import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Activity,
  Zap,
  ArrowUpRight,
  Search,
  XCircle,
  Clock,
  ChevronDown,
} from 'lucide-react';

import type { Execution } from '@/lib/graphql/generated';
// eslint-disable-next-line import-x/order
import { cn } from '@/lib/utils';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function shortEntry(entry: string): string {
  return entry
    .replace(/\.js$|\.ts$/, '')
    .split(/[/\\]/)
    .slice(-2)
    .join('/');
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function normalizeErrorLabel(exec: Execution): string {
  const preview = exec.preview?.label;
  if (preview != null && preview !== 'unknown error') {
    if (preview.toLowerCase().includes('timeout')) return 'Timeout';
    if (preview.toLowerCase().includes('rate limit')) return 'Rate Limit';
    if (preview.toLowerCase().includes('auth') || preview.toLowerCase().includes('unauthorized'))
      return 'Auth Failure';
    if (preview.toLowerCase().includes('not found')) return 'Not Found';
    if (preview.toLowerCase().includes('runtime') || preview.toLowerCase().includes('exception'))
      return 'Runtime Error';
    return preview.length > 40 ? preview.slice(0, 40) + '…' : preview;
  }
  return 'Runtime Error';
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Filters {
  keyword: string;
  status: '' | 'error' | 'success';
  functionName: string;
  errorType: string;
  minDuration: number;
  maxDuration: number;
  afterMs: number;
  beforeMs: number;
}

const EMPTY: Filters = {
  keyword: '',
  status: '',
  functionName: '',
  errorType: '',
  minDuration: 0,
  maxDuration: 0,
  afterMs: 0,
  beforeMs: 0,
};

const DURATION_PRESETS = [
  { label: 'Any duration', min: 0, max: 0 },
  { label: 'Fast (<100ms)', min: 0, max: 100 },
  { label: 'Normal (100–500ms)', min: 100, max: 500 },
  { label: 'Slow (500ms–1s)', min: 500, max: 1000 },
  { label: 'Very slow (>1s)', min: 1000, max: 0 },
  { label: 'Critical (>3s)', min: 3000, max: 0 },
];

const TIME_PRESETS = [
  { label: 'Last 5 minutes', getMs: (): number => Date.now() - 5 * 60_000 },
  { label: 'Last 30 minutes', getMs: (): number => Date.now() - 30 * 60_000 },
  { label: 'Last 1 hour', getMs: (): number => Date.now() - 3600_000 },
  { label: 'Last 6 hours', getMs: (): number => Date.now() - 6 * 3600_000 },
  {
    label: 'Today',
    getMs: (): number => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    },
  },
];

// ─── Dropdown ─────────────────────────────────────────────────────────────────

function FilterDropdown({
  label,
  active,
  icon,
  children,
}: {
  label: string;
  active?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}): import('react/jsx-runtime').JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const fn = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return (): void => document.removeEventListener('mousedown', fn);
  }, []);
  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={(): void => setOpen((v): boolean => !v)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg border text-[11px] font-medium transition-all h-9',
          (active ?? false)
            ? 'bg-blue-500/15 border-blue-500/30 text-blue-300'
            : 'bg-[#0d1117] border-white/10 text-muted-foreground/70 hover:border-white/20 hover:text-foreground',
        )}
      >
        {icon}
        <span>{label}</span>
        <ChevronDown
          className={cn(
            'h-3 w-3 transition-transform text-muted-foreground/40',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1.5 min-w-[210px] bg-[#0d1117] border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.7)] z-50 overflow-hidden">
          {children}
        </div>
      )}
    </div>
  );
}

function DItem({
  label,
  sublabel,
  active,
  onClick,
  cls,
}: {
  label: string;
  sublabel?: string | undefined;
  active?: boolean | undefined;
  onClick: () => void;
  cls?: string | undefined;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between px-3.5 py-2.5 text-[12px] text-left transition-colors',
        (active ?? false) ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]',
      )}
    >
      <span className={cn('font-medium', cls ?? 'text-foreground/80')}>{label}</span>
      <div className="flex items-center gap-1.5">
        {sublabel != null && (
          <span className="text-[10px] font-mono text-muted-foreground/40">{sublabel}</span>
        )}
        {(active ?? false) && <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
      </div>
    </button>
  );
}

function DLabel({ label }: { label: string }): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="px-3.5 pt-2.5 pb-1 text-[9px] font-mono text-muted-foreground/30 uppercase tracking-widest">
      {label}
    </div>
  );
}

// ─── Status Segmented Control ─────────────────────────────────────────────────

function StatusTabs({
  value,
  onChange,
}: {
  value: '' | 'error' | 'success';
  onChange: (v: '' | 'error' | 'success') => void;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="flex bg-[#0d1117] border border-white/10 rounded-lg p-0.5 h-9">
      {[
        { v: '', label: 'All' },
        { v: 'error' as const, label: '❌ Errors' },
        { v: 'success' as const, label: '✅ Success' },
      ].map((opt): import('react/jsx-runtime').JSX.Element => (
        <button
          key={opt.v}
          onClick={(): void => onChange(opt.v as typeof value)}
          className={cn(
            'px-3 py-1 rounded-md text-[11px] font-medium transition-all',
            value === opt.v
              ? opt.v === 'error'
                ? 'bg-[var(--flux-error)]/15 text-[var(--flux-error)] border border-[var(--flux-error)]/20'
                : opt.v === 'success'
                  ? 'bg-[var(--flux-success)]/15 text-[var(--flux-success)] border border-[var(--flux-success)]/20'
                  : 'bg-white/8 text-foreground border border-white/10'
              : 'text-muted-foreground/50 hover:text-muted-foreground border border-transparent',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Filter Pill ──────────────────────────────────────────────────────────────

function FPill({
  label,
  onRemove,
  cls,
}: {
  label: string;
  onRemove: () => void;
  cls?: string;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <span
      className={cn(
        'flex items-center gap-1.5 pl-2.5 pr-1.5 py-0.5 rounded-full border text-[10px] font-mono',
        cls ?? 'bg-white/5 border-white/10 text-foreground/60',
      )}
    >
      {label}
      <button onClick={onRemove} className="hover:text-white transition-colors">
        <XCircle className="h-3 w-3" />
      </button>
    </span>
  );
}

// ─── Execution Row ────────────────────────────────────────────────────────────

function ExecRow({
  exec,
  projectId,
}: {
  exec: Execution;
  projectId: string;
}): import('react/jsx-runtime').JSX.Element {
  const isError = exec.status === 'error';
  const durationMs = exec.durationMs ?? 0;
  const isSlow = durationMs > 1000;
  const preview = exec.preview?.label;

  return (
    <Link
      to={`/projects/${projectId}/executions/${exec.executionId ?? ''}`}
      data-testid={`execution-link-${exec.executionId ?? ''}`}
      data-execution-id={exec.executionId ?? ''}
      className={cn(
        'group flex items-center gap-3 px-4 py-3 border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors last:border-0',
        isError && 'bg-[var(--flux-error)]/[0.015]',
      )}
    >
      {/* Status dot */}
      <span
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded-full shrink-0',
          isError ? 'bg-[var(--flux-error)]/10' : 'bg-[var(--flux-success)]/10',
        )}
      >
        {isError ? (
          <XCircle className="h-3 w-3 text-[var(--flux-error)]" />
        ) : (
          <CheckCircle2 className="h-3 w-3 text-[var(--flux-success)]" />
        )}
      </span>

      {/* Entry */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground truncate">
            {shortEntry(exec.entry ?? 'unknown')}
          </span>
          {isError && preview != null && (
            <span className="text-[10px] font-mono text-[var(--flux-error)]/70 truncate hidden sm:block">
              {preview}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] font-mono text-muted-foreground/40">
            {exec.requestId?.slice(0, 16) ?? exec.executionId?.slice(0, 16)}
          </span>
        </div>
      </div>

      {/* Status badge */}
      <span
        className={cn(
          'px-2 py-0.5 rounded-[4px] text-[9px] font-mono tracking-widest uppercase border shrink-0',
          isError
            ? 'bg-[var(--flux-error)]/15 border-[var(--flux-error)]/30 text-[var(--flux-error)]'
            : 'bg-[var(--flux-success)]/15 border-[var(--flux-success)]/30 text-[var(--flux-success)]',
        )}
      >
        {exec.status}
      </span>

      {/* Duration */}
      <span
        className={cn(
          'font-mono text-xs shrink-0 tabular-nums',
          isError
            ? 'text-[var(--flux-error)]'
            : isSlow
              ? 'text-[var(--flux-warning)]'
              : 'text-muted-foreground/60',
        )}
      >
        {formatDuration(exec.durationMs ?? 0)}
      </span>

      {/* Time ago */}
      <span className="text-[11px] text-muted-foreground/40 shrink-0 font-mono">
        {exec.completedAt != null ? timeAgo(exec.completedAt) : '--'}
      </span>

      <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground/0 group-hover:text-muted-foreground/40 transition-colors shrink-0" />
    </Link>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

import { useExecutionStore } from '@/lib/storage/provider';

export function ProjectExecutionExplorer({
  projectId,
  projectName: _projectName,
}: {
  projectId: string;
  projectName: string;
}): import('react/jsx-runtime').JSX.Element {
  const { executions: allStoreExecutions, forcePoll } = useExecutionStore();
  const [f, setF] = useState<Filters>(EMPTY);
  const setField = useCallback(
    <K extends keyof Filters>(key: K, val: Filters[K]): void =>
      setF((prev) => ({ ...prev, [key]: val })),
    [],
  );
  const clearAll = (): void => setF(EMPTY);

  const projectExecutions = useMemo(
    () => allStoreExecutions.filter((e): boolean => e.projectId === projectId),
    [allStoreExecutions, projectId],
  );

  const { data: fallbackData } = useQuery({
    queryKey: ['project-executions-fallback', projectId],
    enabled: projectId.length > 0 && projectExecutions.length === 0,
    queryFn: async () => {
      const to = Date.now();
      const from = to - 24 * 60 * 60 * 1000;
      // Debug signal (E2E/ops): this should be rare and brief; if it becomes steady-state
      // it likely indicates SSE project discovery / stream coupling is broken.
      // eslint-disable-next-line no-console
      console.debug('E2E_FALLBACK executions_rest_used');
      const res = await fetch(
        `/api/executions?projectId=${encodeURIComponent(projectId)}&from=${from}&to=${to}&limit=200`,
      );
      if (!res.ok) throw new Error(`fallback executions fetch failed: ${res.status}`);
      const json = (await res.json()) as { executions?: unknown[] };
      return Array.isArray(json.executions) ? json.executions : [];
    },
    staleTime: 5_000,
    retry: 1,
  });

  const effectiveProjectExecutions =
    (projectExecutions.length > 0
      ? projectExecutions
      : (fallbackData as typeof projectExecutions | undefined)) ?? projectExecutions;

  // Foundational: when landing on a project-specific executions page, ensure the
  // execution store actively re-discovers project IDs and reconnects the stream.
  useEffect(() => {
    forcePoll();
  }, [forcePoll, projectId]);

  const hasFilters = JSON.stringify(f) !== JSON.stringify(EMPTY);

  // Derive facets from real data
  const fnFacets = useMemo(() => {
    const m = new Map<string, { name: string; short: string; count: number; errors: number }>();
    for (const e of effectiveProjectExecutions) {
      const name = e.entry ?? 'unknown';
      const cur = m.get(name) ?? { name, short: shortEntry(name), count: 0, errors: 0 };
      cur.count++;
      if (e.status === 'error') cur.errors++;
      m.set(name, cur);
    }
    return [...m.values()].sort((a, b): number => b.count - a.count);
  }, [effectiveProjectExecutions]);

  const errorTypeFacets = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of effectiveProjectExecutions.filter((e): boolean => e.status === 'error')) {
      const label = normalizeErrorLabel(e);
      m.set(label, (m.get(label) ?? 0) + 1);
    }
    return [...m.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b): number => b.count - a.count);
  }, [effectiveProjectExecutions]);

  // Apply all filters client-side
  const results = useMemo(() => {
    let list = [...effectiveProjectExecutions];

    if (f.status) list = list.filter((e): boolean => e.status === f.status);
    if (f.functionName) list = list.filter((e): boolean => (e.entry ?? '') === f.functionName);
    if (f.errorType)
      list = list.filter(
        (e): boolean => e.status === 'error' && normalizeErrorLabel(e) === f.errorType,
      );
    if (f.minDuration > 0) list = list.filter((e): boolean => (e.durationMs ?? 0) >= f.minDuration);
    if (f.maxDuration > 0) list = list.filter((e): boolean => (e.durationMs ?? 0) < f.maxDuration);
    if (f.afterMs > 0)
      list = list.filter(
        (e): boolean => e.receivedAt != null && new Date(e.receivedAt).getTime() >= f.afterMs,
      );
    if (f.beforeMs > 0)
      list = list.filter(
        (e): boolean => e.receivedAt != null && new Date(e.receivedAt).getTime() <= f.beforeMs,
      );
    if (f.keyword.trim()) {
      const q = f.keyword.toLowerCase();
      list = list.filter((e): boolean =>
        [
          e.executionId,
          e.requestId ?? '',
          e.entry ?? '',
          e.preview?.label ?? '',
          ...Object.values(e.meta ?? {}).map(String),
        ]
          .join(' ')
          .toLowerCase()
          .includes(q),
      );
    }

    // Rank: errors first, then slow, then newest
    list.sort((a, b): number => {
      const ae = a.status === 'error' ? 1 : 0,
        be = b.status === 'error' ? 1 : 0;
      if (ae !== be) return be - ae;
      const ad = a.durationMs ?? 0,
        bd = b.durationMs ?? 0;
      if (ad !== bd) return bd - ad;
      const at = a.receivedAt != null ? new Date(a.receivedAt).getTime() : 0;
      const bt = b.receivedAt != null ? new Date(b.receivedAt).getTime() : 0;
      return bt - at;
    });
    return list;
  }, [effectiveProjectExecutions, f]);

  const errorCount = results.filter((e): boolean => e.status === 'error').length;
  const slowCount = results.filter((e): boolean => (e.durationMs ?? 0) > 1000).length;

  // Label helpers
  const fnShort = f.functionName ? shortEntry(f.functionName) : null;
  const durLabel = DURATION_PRESETS.find(
    (p): boolean => p.min === f.minDuration && p.max === f.maxDuration,
  )?.label;
  const timeLabel = useMemo((): string | null => {
    if (!f.afterMs) return null;
    const preset = TIME_PRESETS.find((p): boolean => Math.abs(p.getMs() - f.afterMs) < 5000);
    if (preset) return preset.label;
    return `${new Date(f.afterMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} → ${f.beforeMs ? new Date(f.beforeMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'now'}`;
  }, [f.afterMs, f.beforeMs]);

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total', value: projectExecutions.length, sub: 'executions' },
          {
            label: 'Errors',
            value: projectExecutions.filter((e): boolean => e.status === 'error').length,
            sub: 'failures',
            cls:
              projectExecutions.filter((e): boolean => e.status === 'error').length > 0
                ? 'text-[var(--flux-error)]'
                : undefined,
          },
          {
            label: 'Avg Latency',
            value:
              projectExecutions.length > 0
                ? `${Math.round(projectExecutions.reduce((s, e): number => s + (e.durationMs ?? 0), 0) / projectExecutions.length)}ms`
                : '—',
            sub: 'mean',
          },
        ].map((s): import('react/jsx-runtime').JSX.Element => (
          <div key={s.label} className="bg-[#0d1117] border border-white/8 rounded-xl px-4 py-3">
            <div className="text-[9px] font-mono text-muted-foreground/40 uppercase tracking-widest mb-1">
              {s.label}
            </div>
            <div className={cn('text-xl font-bold font-mono', s.cls ?? 'text-foreground')}>
              {s.value}
            </div>
            <div className="text-[9px] text-muted-foreground/30 mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 h-11 px-4 bg-[#0d1117] border border-white/10 rounded-xl hover:border-white/15 focus-within:border-blue-500/30 transition-all">
        <Search className="h-4 w-4 text-muted-foreground/40 shrink-0" />
        <input
          data-testid="executions-keyword"
          value={f.keyword}
          onChange={(e): void => setField('keyword', e.target.value)}
          placeholder="Search by request ID, function name, or error keyword…"
          className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/30 outline-none"
        />
        {f.keyword && (
          <button
            onClick={(): void => setField('keyword', '')}
            className="text-muted-foreground/30 hover:text-muted-foreground"
          >
            <XCircle className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Visual filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <StatusTabs value={f.status} onChange={(v): void => setField('status', v)} />

        <div className="h-7 w-px bg-white/8 mx-0.5" />

        {/* Function */}
        {fnFacets.length > 0 && (
          <FilterDropdown
            label={fnShort ?? 'Function'}
            active={!(fnShort == null)}
            icon={<Zap className="h-3.5 w-3.5 text-muted-foreground/50" />}
          >
            <DItem
              label="Any function"
              active={!f.functionName}
              onClick={(): void => setField('functionName', '')}
            />
            <DLabel label="Your functions" />
            {fnFacets.map((fn): import('react/jsx-runtime').JSX.Element => (
              <DItem
                key={fn.name}
                label={fn.short}
                sublabel={fn.errors > 0 ? `${fn.errors} errors` : `${fn.count} calls`}
                active={f.functionName === fn.name}
                onClick={(): void =>
                  setField('functionName', f.functionName === fn.name ? '' : fn.name)
                }
                cls={fn.errors > 0 ? 'text-orange-400' : undefined}
              />
            ))}
          </FilterDropdown>
        )}

        {/* Error type */}
        {errorTypeFacets.length > 0 && (
          <FilterDropdown
            label={f.errorType || 'Error Type'}
            active={!!f.errorType}
            icon={<AlertTriangle className="h-3.5 w-3.5 text-muted-foreground/50" />}
          >
            <DItem
              label="Any error type"
              active={!f.errorType}
              onClick={(): void => setField('errorType', '')}
            />
            <DLabel label="Detected in your data" />
            {errorTypeFacets.map((et): import('react/jsx-runtime').JSX.Element => (
              <DItem
                key={et.label}
                label={et.label}
                sublabel={`${et.count}×`}
                active={f.errorType === et.label}
                onClick={(): void =>
                  setField('errorType', f.errorType === et.label ? '' : et.label)
                }
                cls="text-[var(--flux-error)]"
              />
            ))}
          </FilterDropdown>
        )}

        {/* Time Range */}
        <FilterDropdown
          label={timeLabel ?? 'Time Range'}
          active={!(timeLabel == null)}
          icon={<Clock className="h-3.5 w-3.5 text-muted-foreground/50" />}
        >
          <DItem
            label="Any time"
            active={!f.afterMs}
            onClick={(): void => {
              setField('afterMs', 0);
              setField('beforeMs', 0);
            }}
          />
          <DLabel label="Quick select" />
          {TIME_PRESETS.map((p): import('react/jsx-runtime').JSX.Element => (
            <DItem
              key={p.label}
              label={p.label}
              active={Math.abs(f.afterMs - p.getMs()) < 5000}
              onClick={(): void => {
                setField('afterMs', p.getMs());
                setField('beforeMs', 0);
              }}
            />
          ))}
          <DLabel label="Custom window (today)" />
          <div className="px-3.5 pb-3 flex gap-2">
            <div className="flex-1">
              <label className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-wide block mb-1">
                From
              </label>
              <input
                type="time"
                className="w-full bg-[#1a1b1e] border border-white/10 rounded-md px-2 py-1.5 text-[11px] font-mono text-foreground/80 outline-none focus:border-blue-500/30"
                onChange={(e): void => {
                  if (!e.target.value) return setField('afterMs', 0);
                  const [h, m] = e.target.value.split(':').map(Number);
                  const d = new Date();
                  d.setHours(h!, m, 0, 0);
                  setField('afterMs', d.getTime());
                }}
              />
            </div>
            <div className="flex-1">
              <label className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-wide block mb-1">
                To
              </label>
              <input
                type="time"
                className="w-full bg-[#1a1b1e] border border-white/10 rounded-md px-2 py-1.5 text-[11px] font-mono text-foreground/80 outline-none focus:border-blue-500/30"
                onChange={(e): void => {
                  if (!e.target.value) return setField('beforeMs', 0);
                  const [h, m] = e.target.value.split(':').map(Number);
                  const d = new Date();
                  d.setHours(h!, m, 59, 999);
                  setField('beforeMs', d.getTime());
                }}
              />
            </div>
          </div>
        </FilterDropdown>

        {/* Duration */}
        <FilterDropdown
          label={durLabel != null && durLabel !== 'Any duration' ? durLabel : 'Duration'}
          active={!(durLabel == null) && durLabel !== 'Any duration'}
          icon={<Activity className="h-3.5 w-3.5 text-muted-foreground/50" />}
        >
          {DURATION_PRESETS.map((p): import('react/jsx-runtime').JSX.Element => (
            <DItem
              key={p.label}
              label={p.label}
              active={f.minDuration === p.min && f.maxDuration === p.max}
              onClick={(): void => {
                setField('minDuration', p.min);
                setField('maxDuration', p.max);
              }}
              cls={p.min >= 1000 ? 'text-[var(--flux-warning)]' : undefined}
            />
          ))}
        </FilterDropdown>

        {hasFilters && (
          <button
            onClick={clearAll}
            className="ml-auto text-[11px] font-mono text-muted-foreground/40 hover:text-[var(--flux-error)] flex items-center gap-1.5 transition-colors"
          >
            <XCircle className="h-3.5 w-3.5" /> Clear all
          </button>
        )}
      </div>

      {/* Active pills */}
      {hasFilters && (
        <div className="flex items-center gap-2 flex-wrap -mt-1">
          {f.status && (
            <FPill
              label={f.status === 'error' ? '❌ Errors only' : '✅ Success only'}
              onRemove={(): void => setField('status', '')}
              cls={
                f.status === 'error'
                  ? 'bg-[var(--flux-error)]/10 border-[var(--flux-error)]/20 text-[var(--flux-error)]'
                  : 'bg-[var(--flux-success)]/10 border-[var(--flux-success)]/20 text-[var(--flux-success)]'
              }
            />
          )}
          {fnShort != null && (
            <FPill label={`fn: ${fnShort}`} onRemove={(): void => setField('functionName', '')} />
          )}
          {f.errorType && (
            <FPill
              label={`error: ${f.errorType}`}
              onRemove={(): void => setField('errorType', '')}
              cls="bg-[var(--flux-error)]/10 border-[var(--flux-error)]/20 text-[var(--flux-error)]/80"
            />
          )}
          {timeLabel != null && (
            <FPill
              label={`⏱ ${timeLabel}`}
              onRemove={(): void => {
                setField('afterMs', 0);
                setField('beforeMs', 0);
              }}
              cls="bg-blue-500/10 border-blue-500/20 text-blue-400"
            />
          )}
          {durLabel != null && durLabel !== 'Any duration' && (
            <FPill
              label={durLabel}
              onRemove={(): void => {
                setField('minDuration', 0);
                setField('maxDuration', 0);
              }}
              cls="bg-[var(--flux-warning)]/10 border-[var(--flux-warning)]/20 text-[var(--flux-warning)]"
            />
          )}
        </div>
      )}

      {/* Result count */}
      <div className="flex items-center gap-4 text-[11px] font-mono pb-1">
        <span className="text-muted-foreground/50">
          <span className="text-foreground font-semibold text-sm">{results.length}</span> results
          {results.length !== projectExecutions.length && (
            <span className="text-muted-foreground/30 ml-0.5">(of {projectExecutions.length})</span>
          )}
        </span>
        {errorCount > 0 && (
          <span className="flex items-center gap-1 text-[var(--flux-error)]">
            <XCircle className="h-3 w-3" />
            {errorCount} errors
          </span>
        )}
        {slowCount > 0 && (
          <span className="flex items-center gap-1 text-[var(--flux-warning)]">
            <Clock className="h-3 w-3" />
            {slowCount} slow
          </span>
        )}
      </div>

      {/* Results */}
      {results.length === 0 ? (
        <div className="py-20 flex flex-col items-center justify-center border border-white/5 rounded-2xl bg-[#0d1117]/40 text-center">
          <Search className="h-8 w-8 text-muted-foreground/20 mb-4" />
          <p className="text-sm font-medium text-foreground/40">No executions match</p>
          <p className="text-xs text-muted-foreground/30 mt-1 max-w-[240px]">
            {hasFilters
              ? 'Try loosening your filters.'
              : 'Run your function and it will appear here.'}
          </p>
          {hasFilters && (
            <button
              onClick={clearAll}
              className="mt-4 px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-[11px] font-mono text-blue-400 hover:bg-blue-500/15 transition-colors"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden">
          {results.map((exec): import('react/jsx-runtime').JSX.Element => (
            <ExecRow key={exec.executionId} exec={exec} projectId={projectId} />
          ))}
        </div>
      )}
    </div>
  );
}
